import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Package,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";
import { brandService } from "../../lib/brandService";
import { toast, Toaster } from "sonner";
import { useNavigate } from "react-router-dom";
import Header from "../Header";
import ConfirmationModal from "../ui/ConfirmationModal";
import CreateBrandModal from "./CreateBrandModal";

const BrandManagement = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [expandedBrand, setExpandedBrand] = useState(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const result = await brandService.getAllBrands();
      if (result.success) {
        setBrands(result.data);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error("Failed to load brands");
      console.error("Error loading brands:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedBrand(null);
    setShowCreateModal(true);
  };

  const handleEdit = (brand) => {
    setSelectedBrand(brand);
    setShowEditModal(true);
  };

  const handleDelete = (brand) => {
    setBrandToDelete(brand);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!brandToDelete) return;

    setIsDeleting(true);
    try {
      const result = await brandService.deleteBrand(brandToDelete.id);
      if (result.success) {
        toast.success("Brand deleted successfully");
        await loadBrands();
        setShowDeleteModal(false);
        setBrandToDelete(null);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error("Failed to delete brand");
      console.error("Error deleting brand:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalSuccess = async () => {
    await loadBrands();
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedBrand(null);
  };

  const toggleBrandStatus = async (brand) => {
    try {
      const result = await brandService.updateBrand(brand.id, {
        ...brand,
        is_active: !brand.is_active,
      });

      if (result.success) {
        toast.success(
          `Brand ${!brand.is_active ? "activated" : "deactivated"}`
        );
        await loadBrands();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error("Failed to update brand status");
      console.error("Error updating brand status:", error);
    }
  };

  const handleBrandClick = (brand) => {
    // Navigate to brand options page (Products or Edit Commissions)
    navigate(`/brands/${brand.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black">
      <Header />

      {/* Sonner Toaster */}
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            color: "white",
          },
        }}
      />

      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#B8460E] rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-[#8B3A0E] rounded-full blur-3xl opacity-15 animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Brand Management
              </h1>
              <p className="text-gray-400 mt-2">
                Manage your brands and their associated products
              </p>
            </div>
            <button
              onClick={handleCreate}
              className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Brand
            </button>
          </div>

          {/* Brands Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 group cursor-pointer"
                onClick={() => handleBrandClick(brand)}
              >
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute w-32 h-32 bg-[#B8460E]/5 rounded-full blur-2xl animate-pulse -top-8 -right-8"></div>
                  <div className="absolute w-24 h-24 bg-[#8B3A0E]/5 rounded-full blur-xl animate-pulse -bottom-4 -left-4"></div>
                </div>

                <div className="relative z-10 p-6">
                  {/* Brand Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-[#B8460E]/20 rounded-lg">
                          <Package className="w-5 h-5 text-[#B8460E]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-white group-hover:text-[#B8460E] transition-colors">
                              {brand.name}
                            </h3>
                            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#B8460E] transition-colors" />
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                brand.is_active
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-red-500/20 text-red-400"
                              }`}
                            >
                              {brand.is_active ? "Active" : "Inactive"}
                            </span>
                            <span className="text-xs text-gray-400">
                              {brand.products?.length || 0} products
                            </span>
                          </div>
                        </div>
                      </div>
                      {brand.description && (
                        <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                          {brand.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Products Preview */}
                  {brand.products && brand.products.length > 0 && (
                    <div className="mb-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedBrand(
                            expandedBrand === brand.id ? null : brand.id
                          );
                        }}
                        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-2"
                      >
                        <Package className="w-4 h-4" />
                        Products ({brand.products.length})
                        {expandedBrand === brand.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>

                      {expandedBrand === brand.id && (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {brand.products.map((product) => (
                            <div
                              key={product.id}
                              className="flex items-center justify-between p-2 bg-white/5 rounded-lg"
                            >
                              <span className="text-sm text-white">
                                {product.name}
                              </span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  product.is_active
                                    ? "bg-green-500/20 text-green-400"
                                    : "bg-red-500/20 text-red-400"
                                }`}
                              >
                                {product.is_active ? "Active" : "Inactive"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(brand);
                        }}
                        className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all duration-200"
                        title="Edit Brand"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBrandStatus(brand);
                        }}
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          brand.is_active
                            ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        }`}
                        title={brand.is_active ? "Deactivate" : "Activate"}
                      >
                        {brand.is_active ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(brand);
                        }}
                        className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all duration-200"
                        title="Delete Brand"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      <span>Click to view products</span>
                      <ExternalLink className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {brands.length === 0 && (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                No brands yet
              </h3>
              <p className="text-gray-400 mb-6">
                Create your first brand to get started
              </p>
              <button
                onClick={handleCreate}
                className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Create Brand
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <CreateBrandModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedBrand(null);
        }}
        onSuccess={handleModalSuccess}
        editBrand={selectedBrand}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setBrandToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Brand"
        message={`Are you sure you want to delete "${brandToDelete?.name}"? This will not delete the associated products.`}
        confirmText="Delete Brand"
        cancelText="Cancel"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
};

export default BrandManagement;
